/** DTO REST API — общие для сервера и клиента. Даты — ISO-строки. */
import type { RoleId, Team, Winner } from './roles';
import type { LogEntry } from './engine/types';
import type { GameSettings } from './settings';
import type { GameKind } from './kinds';

export type UserRole = 'PLAYER' | 'ADMIN';

export interface MeDto {
  id: string;
  email: string;
  login: string;
  nickname: string;
  avatarId: string | null;
  telegram: string | null;
  role: UserRole;
  createdAt: string;
  /** Дано ли согласие на обработку персональных данных в текущей редакции (PD_CONSENT_VERSION). */
  pdConsent: boolean;
}

export interface AuthResponse {
  accessToken: string;
  user: MeDto;
}

export interface ApiErrorBody {
  error: string;
  message: string;
  /** Ошибки валидации по полям. */
  fields?: Record<string, string>;
  /** Для BANNED. */
  bannedUntil?: string | null;
  banReason?: string | null;
}

export interface RoleStatDto {
  roleId: RoleId;
  played: number;
  won: number;
  survived: number;
  points: number;
  winRate: number;
}

export interface GameHistoryItem {
  gameId: string;
  kind: GameKind;
  endedAt: string | null;
  role: RoleId;
  team: Team;
  winner: Winner | null;
  isWinner: boolean;
  points: number;
  playerCount: number;
  daysPlayed: number;
}

export interface ProfileDto {
  id: string;
  login: string;
  nickname: string;
  avatarId: string | null;
  telegram: string | null;
  role: UserRole;
  createdAt: string;
  /** Все игры вместе. */
  totals: ProfileTotals;
  byRole: RoleStatDto[];
  /** То же отдельно по видам игр (рейтинг тоже раздельный). */
  byKind: Record<GameKind, { totals: ProfileTotals; byRole: RoleStatDto[] }>;
  recentGames: GameHistoryItem[];
}

export interface ProfileTotals {
  played: number;
  won: number;
  lost: number;
  winRate: number;
  points: number;
}

export interface RoomListItem {
  id: string;
  code: string;
  name: string;
  kind: GameKind;
  isPrivate: boolean;
  hostNickname: string;
  members: number;
  maxPlayers: number;
  inGame: boolean;
  createdAt: string;
}

export interface CreatedRoomDto {
  id: string;
  code: string;
}

export interface GameParticipantDto {
  seat: number;
  /** null — игрок удалил аккаунт. */
  userId: string | null;
  nickname: string;
  role: RoleId;
  team: Team;
  survived: boolean | null;
  deathReason: 'NIGHT_KILL' | 'VOTE' | 'LEFT' | null;
  deathDay: number | null;
  isWinner: boolean | null;
  points: number;
  pointsBreakdown: { reason: string; points: number; day: number }[];
}

export interface GameSummaryDto {
  id: string;
  kind: GameKind;
  roomName: string | null;
  status: 'IN_PROGRESS' | 'FINISHED' | 'ABORTED';
  winner: Winner | null;
  playerCount: number;
  daysPlayed: number;
  startedAt: string;
  endedAt: string | null;
  settings: GameSettings;
  moderatorNickname: string | null;
  participants: GameParticipantDto[];
}

export interface GameLogDto extends GameSummaryDto {
  log: (Omit<LogEntry, 'at'> & { at: string })[];
}

// ---------------- Admin ----------------

export interface AdminDashboardDto {
  online: number;
  activeRooms: number;
  activeGames: number;
  users: { total: number; newToday: number; banned: number; admins: number };
  games: { finished: number; finishedToday: number; aborted: number };
  winners: Record<Winner, number>;
  rooms: AdminRoomDto[];
}

export interface AdminRoomDto {
  id: string;
  code: string;
  name: string;
  isPrivate: boolean;
  hostNickname: string;
  members: number;
  maxPlayers: number;
  game: { id: string; phase: string; day: number; alive: number; players: number } | null;
}

export interface AdminUserDto {
  id: string;
  email: string;
  login: string;
  nickname: string;
  avatarId: string | null;
  telegram: string | null;
  role: UserRole;
  createdAt: string;
  lastSeenAt: string | null;
  bannedAt: string | null;
  bannedUntil: string | null;
  banReason: string | null;
  isBanned: boolean;
  online: boolean;
}

export interface AdminUserDetailDto extends AdminUserDto {
  profile: ProfileDto;
  audit: { id: string; action: string; actorNickname: string; reason: string | null; createdAt: string }[];
}

export interface AdminGameListItem {
  id: string;
  kind: GameKind;
  roomName: string | null;
  status: 'IN_PROGRESS' | 'FINISHED' | 'ABORTED';
  winner: Winner | null;
  playerCount: number;
  daysPlayed: number;
  startedAt: string;
  endedAt: string | null;
}

export interface RatingRow {
  rank: number;
  userId: string;
  nickname: string;
  avatarId: string | null;
  points: number;
  played: number;
  won: number;
  winRate: number;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** GET /api/site/status — открыт ли сайт для обычных игроков (заглушка ADMIN_GATE). */
export interface SiteStatusDto {
  open: boolean;
}

/** Редакция текстов согласия и политики. Сменили текст — меняем версию, и сайт заново спросит согласие. */
export const PD_CONSENT_VERSION = '2026-10-19';

/** GET /api/legal — данные оператора для страниц «Политика» и «Согласие». */
export interface LegalInfoDto {
  operatorName: string | null;
  operatorEmail: string | null;
  consentVersion: string;
}
